/**
 * HUD：レティクル、注視対象の名前、現在の目標、操作ヒント、通知トースト。
 */

import { clear, el } from './dom';

export interface HudObjective {
  text: string;
  done: boolean;
}

/** shouldShowResumeOverlay に渡す「いまの状態」 */
export interface ResumeOverlayInput {
  /** ゲーム本編の進行中か（タイトル画面なら false） */
  playing: boolean;
  /** 会話・手帳・端末・設定などのモーダルが開いているか */
  anyModalOpen: boolean;
  /** Pointer Lock が実際に掛かっているか */
  pointerLocked: boolean;
}

/**
 * 「クリックで再開」オーバーレイを出すべきか。
 *
 * 【この関数がある理由】
 * 以前は Pointer Lock の「状態が変化したイベント」だけでオーバーレイを出していた。
 * PointerLockManager は状態が変わらなければリスナーを呼ばないため、
 * すでに 'needs-click' の状態で requestPointerLock() が再び失敗しても通知が飛ばず、
 * 「動けないのに案内も出ない」詰み状態になっていた。
 * そこで判定を状態遷移から切り離し、毎フレーム現在の状態から出し直す。
 *
 * 状態名（'needs-click' / 'unlocked'）では分岐しない。
 * 'unlocked' でも「モーダルが閉じているのにロックされていない」なら
 * プレイヤーは同じように動けないので、isLocked が false であることだけを見る。
 */
export function shouldShowResumeOverlay(input: ResumeOverlayInput): boolean {
  return input.playing && !input.anyModalOpen && !input.pointerLocked;
}

export class Hud {
  readonly root: HTMLElement;

  private reticle: HTMLElement;
  private targetLabel: HTMLElement;
  private objectiveBox: HTMLElement;
  private roomLabel: HTMLElement;
  private toasts: HTMLElement;
  private hintPrompt: HTMLElement;
  private resumeOverlay: HTMLElement;

  constructor(onResumeClick: () => void) {
    this.reticle = el('div', { class: 'reticle' });
    this.targetLabel = el('div', { class: 'target-label', hidden: true });
    this.objectiveBox = el('div', { class: 'objective-box' });
    this.roomLabel = el('div', { class: 'room-label' });
    this.toasts = el('div', { class: 'toasts' });
    this.hintPrompt = el('div', { class: 'hint-prompt', hidden: true });

    this.resumeOverlay = el('div', { class: 'resume-overlay', hidden: true }, [
      el('div', { class: 'resume-panel' }, [
        el('p', { class: 'resume-title', text: 'クリックで再開' }),
        el('p', {
          class: 'resume-sub',
          text: 'マウス操作を有効にするため、画面をクリックしてください。（Esc でいつでも解除できます）',
        }),
      ]),
    ]);
    this.resumeOverlay.addEventListener('click', onResumeClick);

    this.root = el('div', { class: 'hud' }, [
      this.reticle,
      this.targetLabel,
      this.objectiveBox,
      this.roomLabel,
      this.toasts,
      this.hintPrompt,
      el('div', { class: 'controls-hint' }, [
        el('span', { text: 'WASD・矢印 移動 / Shift＋矢印 視点 / Shift＋WASD ダッシュ' }),
        el('span', { text: 'E 調べる・話す' }),
        el('span', { text: 'Tab 手帳' }),
        el('span', { text: 'T 端末' }),
        el('span', { text: 'R 電波スキャナー' }),
        el('span', { text: 'M トラフィックモニター' }),
        el('span', { text: 'Esc メニュー' }),
      ]),
      this.resumeOverlay,
    ]);
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
  }

  setResumeOverlay(visible: boolean): void {
    this.resumeOverlay.hidden = !visible;
  }

  setTarget(label: string | null, hint = '[E] 調べる'): void {
    if (!label) {
      this.targetLabel.hidden = true;
      this.reticle.classList.remove('active');
      return;
    }
    clear(this.targetLabel);
    this.targetLabel.append(
      el('span', { class: 'target-name', text: label }),
      el('span', { class: 'target-hint', text: hint }),
    );
    this.targetLabel.hidden = false;
    this.reticle.classList.add('active');
  }

  /**
   * 現在地の表示。
   * 廊下や階段では部屋名が無いため、階数だけの文言（「3F 廊下」など）を受け取る。
   * 文言の組み立ては engine/layout.ts の formatPlaceLabel が行う。
   */
  setRoom(label: string | null): void {
    this.roomLabel.textContent = label ?? '';
  }

  setObjectives(chapterTitle: string, objectives: HudObjective[]): void {
    clear(this.objectiveBox);
    this.objectiveBox.append(el('div', { class: 'objective-chapter', text: chapterTitle }));
    const list = el('ul', { class: 'objective-list' });
    for (const objective of objectives) {
      list.append(
        el('li', { class: objective.done ? 'objective done' : 'objective' }, [
          el('span', { class: 'objective-mark', text: objective.done ? '✓' : '・' }),
          el('span', { text: objective.text }),
        ]),
      );
    }
    this.objectiveBox.append(list);
  }

  setHintPrompt(text: string | null): void {
    if (!text) {
      this.hintPrompt.hidden = true;
      return;
    }
    this.hintPrompt.textContent = text;
    this.hintPrompt.hidden = false;
  }

  /** 手がかり・知識カードの入手を知らせる */
  toast(title: string, body: string, tone: 'clue' | 'card' | 'info' = 'info'): void {
    const node = el('div', { class: `toast toast-${tone}` }, [
      el('div', { class: 'toast-title', text: title }),
      el('div', { class: 'toast-body', text: body }),
    ]);
    this.toasts.append(node);
    window.setTimeout(() => {
      node.classList.add('toast-out');
      window.setTimeout(() => node.remove(), 400);
    }, 4200);
  }
}
